import globalLogger from "@lazarentertainment/js-logger"
import { captureException } from "@sentry/nextjs"
import { useChannelContext } from "client/components/channel/ChannelContext"
import { useConfigContext } from "client/components/configuration/ConfigContext"
import { useParamContext } from "client/components/params/ParamContext"
import dynamic from "next/dynamic"
import { useEffect, useState } from "react"
import {
  desktopSubtitlesFont,
  mobileSubtitlesFont,
} from "shared/styles/mixins"
import styled from "styled-components"

import { useVideoAnalyticsEvents } from "./player/useVideoAnalyticsEvents"
import { useVideoContext } from "./player/VideoContext"
import { PlayerError } from "./types"

const DynamicRtcPlayer = dynamic(() => import("./player/RtcPlayer"), {
  ssr: false,
})
const DynamicHlsPlayer = dynamic(() => import("./player/HlsPlayer"), {
  ssr: false,
})
const DynamicUrlPlayer = dynamic(() => import("./player/UrlPlayer"), {
  ssr: false,
})

export const VideoWrapper = styled.div<{
  $visible: boolean
  $mobileSubtitles: boolean
  $subtitleBottom: number
}>`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  display: ${(props) => (props.$visible ? "flex" : "none")};
  position: relative;
  background-color: rgb(var(--bv-bg-color-video));

  justify-content: center;
  align-items: center;

  ${(props) =>
    props.$mobileSubtitles ? mobileSubtitlesFont : desktopSubtitlesFont}

  // Adjusts vertical position in everything except firefox: In firefox, need to edit VTTCue in js
  > ::-webkit-media-text-track-container {
    transform: translateY(-${(props) => props.$subtitleBottom}px);
  }
`

interface Props {
  mobileSubtitles?: boolean
}

export enum PlaybackSource {
  HLS = "hls",
  WEBRTC = "webrtc",
  ERROR = "error",
  URL = "url",
}

const VideoPlayer = ({ mobileSubtitles = false }: Props) => {
  const [currentPlayerType, setPlayerType] = useState<PlaybackSource>(null)
  const url = null // Change this value to a test video url in order to debug with the URL player
  const {
    playerError,
    setPlayerError,
    subtitleBottom,
    videoElement,
    alreadyPlayed,
  } = useVideoContext()

  const config = useConfigContext()
  const channel = useChannelContext()
  const params = useParamContext()
  const { broadcastStreamToken, hlsUrl, live } = channel

  // Set up analytics calls on certain video events
  const { sendAnalyticsOnDestroy } = useVideoAnalyticsEvents(videoElement)

  // Specifies the order of player types to use as fallbacks
  // TEMPORARILY DISABLE HLS FALLBACK
  const playerTypes = [
    PlaybackSource.WEBRTC,
    PlaybackSource.ERROR,
    // PlaybackSource.HLS,
  ]

  const onFatalError = (err: PlayerError) => {
    setPlayerError(err)
    globalLogger.error(err)
    captureException(err)
    const nextType =
      playerTypes[playerTypes.indexOf(currentPlayerType) + 1]
    if (nextType) {
      setPlayerType(nextType)
    }
  }

  const unmountPlayer = () => {
    sendAnalyticsOnDestroy()
    setPlayerType(null)
    setPlayerError(null)
  }

  useEffect(() => {
    if (currentPlayerType === PlaybackSource.URL) {
      return
    }

    if (!broadcastStreamToken) {
      unmountPlayer()
      return
    }

    if (params.forceHLS) {
      setPlayerType(PlaybackSource.HLS)
      return
    }

    setPlayerType(playerTypes[0])
  }, [broadcastStreamToken])

  return (
    <>
      <VideoWrapper
        className="videoWrapper"
        $visible={alreadyPlayed && live && !playerError}
        $mobileSubtitles={mobileSubtitles}
        $subtitleBottom={subtitleBottom}
      >
        {currentPlayerType === PlaybackSource.WEBRTC &&
          broadcastStreamToken && (
            <DynamicRtcPlayer
              server={config.api.janusUrl}
              webrtcService="wheel"
              streamToken={broadcastStreamToken}
              onFatalError={onFatalError}
              live={live}
              mobileSubtitles={mobileSubtitles}
            />
          )}
        {currentPlayerType === PlaybackSource.HLS && hlsUrl && (
          <DynamicHlsPlayer
            manifestUrl={hlsUrl}
            onFatalError={onFatalError}
            live={live}
            mobileSubtitles={mobileSubtitles}
          />
        )}
        {((currentPlayerType === PlaybackSource.URL && url) ||
          currentPlayerType === PlaybackSource.ERROR) && (
          // Show URL player during error for sizing (plays nothing & is hidden behind error)
          <DynamicUrlPlayer url={url} />
        )}
      </VideoWrapper>
    </>
  )
}

export default VideoPlayer
